import { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Ellipsis, Pencil, Trash2 } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import ACL_RELATIONSHIPS from "acl-relationships";
import ConfirmDeleteDialog from "components/modals/ConfirmDeleteDialog";
import useAuthorized from "hooks/useAuthorized";
import { useNoteTemplateMutations } from "hooks/useNoteTemplateTable";
import { NOTE_TEMPLATES_PATH } from "routes/note_template/NoteTemplateTableConstants";

export function NoteTemplateTableActions({ original }) {
  const navigate = useNavigate();
  const [deleteOpen, setDeleteOpen] = useState(false);

  const canEdit = useAuthorized([ACL_RELATIONSHIPS.noteTemplate.edit]);
  const canDelete = useAuthorized([ACL_RELATIONSHIPS.noteTemplate.delete]);

  const { remove } = useNoteTemplateMutations({
    onDeleted: () => setDeleteOpen(false),
  });

  if (!canEdit && !canDelete) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Note template actions"
          className={buttonVariants({ variant: "ghost", size: "icon-sm" })}
        >
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {canEdit && (
            <DropdownMenuItem
              onClick={() =>
                navigate(`${NOTE_TEMPLATES_PATH}/${original.id}/edit`)
              }
            >
              <Pencil />
              Edit
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete note template?"
        description="Are you sure you want to delete this note template?"
        onConfirm={() => remove.mutate(original.id)}
      />
    </>
  );
}

NoteTemplateTableActions.propTypes = {
  original: PropTypes.shape({
    id: PropTypes.number.isRequired,
  }).isRequired,
};
